/**
 * Bulk download for KB documents. One selected file streams via its signed S3
 * URL; multiple files stream as a single zip through the shared zip-download
 * client. Selection can span pages that aren't loaded, so the manifest is
 * resolved by fetching the KB's full file list when needed.
 */
import { useMutation } from '@tanstack/react-query';
import { getFileDownloadUrl } from '@/shared/api/assets.api';
import {
  sanitizeZipFilename,
  streamZipToDisk,
  type ZipDownloadAsset,
} from '@/shared/api/zipDownload';
import { track } from '@/shared/analytics';
import { i18n } from '@/shared/i18n';
import { downloadFile } from '@/shared/utils/downloadFile';
import type { FileRow, SortableFileRowKeys } from '../../model/documents.types';
import { getFilesByKnowledgeBaseId } from '../knowledgeBases.api';

/**
 * Upper bound on a single bulk download. The whole-KB resolve fetches and zips
 * every target in one pass, so an unbounded selection could stall the tab and
 * hammer the assets-api (which has no server-side `take` cap). Beyond this the
 * user is asked to narrow their selection.
 */
export const MAX_BULK_DOWNLOAD_FILES = 1000;

/** Throws a user-facing error when a selection exceeds {@link MAX_BULK_DOWNLOAD_FILES}. */
export function assertWithinBulkDownloadLimit(count: number): void {
  if (count > MAX_BULK_DOWNLOAD_FILES) {
    throw new Error(
      i18n.t('knowledge:knowledgeBases.knowledgeBaseDownload.bulkDownloadLimit', {
        max: MAX_BULK_DOWNLOAD_FILES,
      }),
    );
  }
}

/** Minimal shape the download pipeline needs from a document. */
export interface DownloadableFile {
  id: string;
  fileName: string;
  mimeType?: string;
  assetId?: string;
}

export interface DownloadKnowledgeBaseFilesInput {
  knowledgeBaseId: string;
  knowledgeBaseName: string;
  selectedIds: Set<string>;
  isAllAcrossPages: boolean;
  /**
   * Ids de-selected while "all across pages" is active. Only meaningful when
   * {@link isAllAcrossPages} is true; the resolved set is every match minus these.
   */
  excludedIds?: Set<string>;
  /** Rows already loaded on the current page — avoids a refetch when possible. */
  loadedRows: FileRow[];
  sortBy: SortableFileRowKeys;
  sortOrder: 'asc' | 'desc';
  search?: string;
}

/**
 * Builds the zip manifest from downloadable files, dropping any without an
 * asset and deduping archive paths so same-named files don't clobber each
 * other inside the zip. `fileSize` is `0`: the assets-api pipeline validates
 * but never reads it.
 */
export function buildZipManifestAssets(files: DownloadableFile[]): ZipDownloadAsset[] {
  const seen = new Map<string, number>();
  const assets: ZipDownloadAsset[] = [];

  for (const file of files) {
    if (!file.assetId) continue;
    assets.push({
      assetId: file.assetId,
      archivePath: dedupeArchivePath(file.fileName, seen),
      fileSize: 0,
    });
  }

  return assets;
}

/** Appends a `(n)` suffix (before the extension) on repeated names. */
function dedupeArchivePath(rawName: string, seen: Map<string, number>): string {
  // eslint-disable-next-line i18next/no-literal-string -- archive entry filename fallback
  const base = rawName.trim() || 'file';
  const count = seen.get(base) ?? 0;
  seen.set(base, count + 1);

  if (count === 0) return base;

  const dot = base.lastIndexOf('.');
  if (dot > 0) {
    return `${base.slice(0, dot)} (${count})${base.slice(dot)}`;
  }
  return `${base} (${count})`;
}

/**
 * Resolves the concrete files to download. Reuses the loaded rows when the whole
 * selection is on the current page; otherwise fetches the matching file list
 * (scoped by `search`) once and filters it. Exported for unit testing.
 */
export async function resolveTargetFiles(
  input: DownloadKnowledgeBaseFilesInput,
): Promise<DownloadableFile[]> {
  const { knowledgeBaseId, selectedIds, isAllAcrossPages, loadedRows } = input;
  const excludedIds = input.excludedIds ?? new Set<string>();

  const loadedById = new Map(loadedRows.map((row) => [row.id, row]));
  const selectionResolvedLocally =
    !isAllAcrossPages && [...selectedIds].every((id) => loadedById.has(id));

  if (selectionResolvedLocally) {
    return [...selectedIds]
      .map((id) => loadedById.get(id))
      .filter((row): row is FileRow => !!row)
      .map(toDownloadable);
  }

  // An explicit cross-page selection has a known size, so guard it up front. For
  // "all across pages" the true size isn't known until the fetch returns — a
  // search may match far fewer than the KB total — so the limit is checked on
  // the fetched result below instead of on an unfiltered count.
  if (!isAllAcrossPages) {
    assertWithinBulkDownloadLimit(selectedIds.size);
  }

  // Selection spans unloaded pages (or is "all"): fetch once and filter.
  // `search` scopes the fetch to the same result set the user sees. Request one
  // past the cap (the assets-api has no server-side `take` limit) so a result
  // set that overflows the limit is detectable rather than silently truncated.
  const fetched = await getFilesByKnowledgeBaseId(knowledgeBaseId, {
    sortBy: input.sortBy,
    sortOrder: input.sortOrder,
    skip: 0,
    take: MAX_BULK_DOWNLOAD_FILES + 1,
    search: input.search,
  });

  if (isAllAcrossPages) {
    // Everything that matched, minus the rows the user de-selected. The fetch is
    // already capped at MAX+1, so assert on the effective (post-exclusion) size.
    const targets = fetched.filter((file) => !excludedIds.has(file.id)).map(toDownloadable);
    assertWithinBulkDownloadLimit(targets.length);
    return targets;
  }
  return fetched.map(toDownloadable).filter((file) => selectedIds.has(file.id));
}

function toDownloadable(file: {
  id: string;
  fileName: string;
  mimeType?: string;
  assetId?: string;
}): DownloadableFile {
  return { id: file.id, fileName: file.fileName, mimeType: file.mimeType, assetId: file.assetId };
}

/**
 * Downloads the selected KB documents. Single downloadable file → signed-URL
 * download; multiple → zip stream. Emits `kb_files_bulk_downloaded` on success.
 * Returns the number of files actually downloaded.
 */
export function useDownloadKnowledgeBaseFiles() {
  return useMutation<number, Error, DownloadKnowledgeBaseFilesInput>({
    mutationFn: async (input) => {
      const targets = (await resolveTargetFiles(input)).filter((file) => !!file.assetId);

      if (targets.length === 0) {
        throw new Error(
          i18n.t('knowledge:knowledgeBases.knowledgeBaseDownload.noDownloadableFiles'),
        );
      }

      const mode: 'single' | 'zip' = targets.length === 1 ? 'single' : 'zip';

      if (mode === 'single') {
        const [file] = targets;
        const url = await getFileDownloadUrl(file.assetId!);
        await downloadFile({ url, mimeType: file.mimeType, fileName: file.fileName });
      } else {
        const manifest = {
          folderName: input.knowledgeBaseName,
          assets: buildZipManifestAssets(targets),
        };
        await streamZipToDisk(manifest, sanitizeZipFilename(input.knowledgeBaseName));
      }

      track('kb_files_bulk_downloaded', {
        kb_id: input.knowledgeBaseId,
        file_count: targets.length,
        mode,
        all_across_pages: input.isAllAcrossPages,
      });

      return targets.length;
    },
  });
}
